import { computed, onMounted, ref, watch } from 'vue'

const STORAGE_KEY = 'simvox-admin-theme'
const THEMES = ['system', 'light', 'dark']

const preference = ref(readStoredPreference())
const isDark = ref(false)
let mediaQuery = null
let mediaListener = null
let initialized = false

function readStoredPreference() {
  if (typeof window === 'undefined') return 'dark'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (THEMES.includes(stored)) return stored
  return 'dark'
}

function systemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return true
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function resolveIsDark(value = preference.value) {
  if (value === 'dark') return true
  if (value === 'light') return false
  return systemPrefersDark()
}

function applyTheme(value = preference.value) {
  const dark = resolveIsDark(value)
  isDark.value = dark

  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.classList.toggle('dark', dark)
  root.dataset.theme = value
  root.style.colorScheme = dark ? 'dark' : 'light'
}

function setTheme(value) {
  if (!THEMES.includes(value)) return
  preference.value = value
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, value)
  }
  applyTheme(value)
}

function cycleTheme() {
  const order = ['dark', 'light', 'system']
  const currentIndex = order.indexOf(preference.value)
  setTheme(order[(currentIndex + 1) % order.length])
}

function ensureMediaListener() {
  if (typeof window === 'undefined' || !window.matchMedia) return
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaListener = () => {
    if (preference.value === 'system') applyTheme('system')
  }
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', mediaListener)
  } else if (mediaQuery.addListener) {
    mediaQuery.addListener(mediaListener)
  }
}

export function initTheme() {
  if (initialized) {
    applyTheme(preference.value)
    return
  }
  initialized = true
  applyTheme(preference.value)
  ensureMediaListener()
}

export function useTheme() {
  onMounted(() => {
    initTheme()
  })

  watch(preference, (value) => applyTheme(value))

  const themeLabel = computed(() => {
    if (preference.value === 'system') return 'System'
    if (preference.value === 'light') return 'Light'
    return 'Dark'
  })

  const themeIcon = computed(() => {
    if (preference.value === 'system') return 'circle-half-stroke'
    if (preference.value === 'light') return 'sun'
    return 'moon'
  })

  return {
    preference,
    isDark,
    themeLabel,
    themeIcon,
    themes: THEMES,
    setTheme,
    cycleTheme,
    applyTheme,
  }
}

export { preference, isDark, setTheme, cycleTheme, applyTheme }
